/**
 * Modal d'analyse IA de la rentabilité
 * Affiche les recommandations générées par l'IA (Claude ou GPT-4)
 * basées sur les données de rentabilité de l'utilisateur
 * L'utilisateur choisit le provider IA à utiliser
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Spin,
  Alert,
  Progress,
  Tag,
  Typography,
  Divider,
  Card,
  List,
  Space,
  Radio
} from 'antd';
import {
  RobotOutlined,
  BulbOutlined,
  WarningOutlined,
  RocketOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { analyticsApi } from '../../utils/api';
// Accent contextuel : noir dans l'espace privé, bleu en dehors.
import { PRIVATE_PRIMARY_COLOR as PRIMARY_COLOR } from '../../theme/privateTheme';

const { Title, Text, Paragraph } = Typography;

/**
 * Composant modal pour l'analyse IA de la rentabilité
 * @param {boolean} visible - Visibilité du modal
 * @param {function} onCancel - Callback de fermeture
 * @param {Array} dateRange - Plage de dates [dayjs, dayjs]
 */
const AIAnalysisModal = ({ visible, onCancel, dateRange }) => {
  // États du composant
  const [loading, setLoading] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  // Providers disponibles sur le serveur
  const [providers, setProviders] = useState({ claude: { available: false }, gpt4: { available: false } });
  // Provider sélectionné par l'utilisateur
  const [selectedProvider, setSelectedProvider] = useState(null);

  /**
   * Charge les providers disponibles à l'ouverture du modal
   */
  useEffect(() => {
    if (visible) {
      loadProviders();
    }
  }, [visible]);

  /**
   * Récupère les providers IA disponibles depuis le serveur
   */
  const loadProviders = async () => {
    setLoadingProviders(true);
    try {
      const response = await analyticsApi.getAIProviders();
      const availableProviders = response.data?.providers || {};
      setProviders(availableProviders);

      // Sélectionner automatiquement le premier provider disponible
      if (availableProviders.claude?.available) {
        setSelectedProvider('claude');
      } else if (availableProviders.gpt4?.available) {
        setSelectedProvider('gpt4');
      } else {
        setSelectedProvider(null);
      }
    } catch (err) {
      console.error('Erreur chargement providers:', err);
      setError({
        code: 'PROVIDER_LOAD_ERROR',
        message: 'Impossible de charger les providers IA disponibles'
      });
    } finally {
      setLoadingProviders(false);
    }
  };

  /**
   * Lance l'analyse IA avec le provider sélectionné
   */
  const handleAnalyze = async () => {
    if (!selectedProvider) {
      setError({
        code: 'NO_PROVIDER',
        message: 'Veuillez sélectionner un provider IA'
      });
      return;
    }

    setLoading(true);
    setError(null);
    setAnalysis(null);

    try {
      // Préparer les paramètres pour l'API
      const params = {
        provider: selectedProvider,
        start_date: dateRange?.[0]?.startOf('day').toISOString() || null,
        end_date: dateRange?.[1]?.endOf('day').toISOString() || null
      };

      const response = await analyticsApi.getAIAnalysis(params);

      if (response.data?.success) {
        setAnalysis(response.data.analysis);
      } else {
        throw new Error('Réponse invalide du serveur');
      }
    } catch (err) {
      console.error('Erreur analyse IA:', err);

      // Extraire le code et message d'erreur
      const errorData = err.response?.data || {};

      // Si limite hebdomadaire atteinte, afficher la dernière analyse enregistrée
      if (errorData.code === 'AI_WEEKLY_LIMIT' && errorData.lastAnalysis?.analysis) {
        setAnalysis(errorData.lastAnalysis.analysis);
        setError({
          code: errorData.code,
          message: errorData.message,
          nextAvailableDate: errorData.nextAvailableDate,
          // Date à laquelle l'analyse a été faite
          analysisDate: errorData.lastAnalysis.createdAt
        });
      } else if (errorData.code === 'AI_WEEKLY_LIMIT') {
        setError({
          code: errorData.code,
          message: errorData.message,
          nextAvailableDate: errorData.nextAvailableDate
        });
      } else {
        setError({
          code: errorData.code || 'UNKNOWN_ERROR',
          message: errorData.message || errorData.error || err.message || 'Une erreur est survenue'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Réinitialise l'état du modal
   */
  const handleReset = () => {
    setAnalysis(null);
    setError(null);
    setLoading(false);
  };

  /**
   * Ferme le modal et réinitialise
   */
  const handleClose = () => {
    handleReset();
    onCancel();
  };

  /**
   * Retourne la couleur du score de santé
   */
  const getHealthScoreColor = (score) => {
    if (score >= 70) return '#52c41a'; // Vert
    if (score >= 40) return '#faad14'; // Orange
    return '#ff4d4f'; // Rouge
  };

  /**
   * Retourne la couleur du tag de priorité
   */
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'haute': return 'red';
      case 'moyenne': return 'orange';
      case 'basse': return 'green';
      default: return 'default';
    }
  };

  /**
   * Retourne l'icône de la catégorie
   */
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Ventes': return '💰';
      case 'Dépenses': return '💸';
      case 'Produits': return '📦';
      case 'Clients': return '👥';
      case 'Marketing': return '📣';
      default: return '📊';
    }
  };

  /**
   * Vérifie si au moins un provider est disponible
   */
  const hasAvailableProvider = providers.claude?.available || providers.gpt4?.available;

  /**
   * Rendu de l'état initial (avant analyse)
   */
  const renderInitialState = () => (
    <div className="text-center py-6">
      <RobotOutlined style={{ fontSize: 64, color: PRIMARY_COLOR, marginBottom: 16 }} />
      <Title level={4}>Analyse IA de votre rentabilité</Title>
      <Paragraph type="secondary" className="mb-4">
        L'IA va analyser vos données financières et vous proposer des recommandations
        personnalisées pour améliorer votre rentabilité.
      </Paragraph>

      {/* Sélecteur de provider IA */}
      {loadingProviders ? (
        <div className="mb-4">
          <Spin size="small" /> Chargement des providers...
        </div>
      ) : hasAvailableProvider ? (
        <div className="mb-6">
          <Text strong className="block mb-3">Choisissez le modèle IA :</Text>
          <Radio.Group
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            buttonStyle="solid"
            size="large"
          >
            {providers.claude?.available && (
              <Radio.Button value="claude">
                🤖 Claude
              </Radio.Button>
            )}
            {providers.gpt4?.available && (
              <Radio.Button value="gpt4">
                🧠 GPT-4
              </Radio.Button>
            )}
          </Radio.Group>
          {selectedProvider && (
            <div className="mt-2">
              <Text type="secondary" className="text-xs">
                Modèle : {selectedProvider === 'claude' ? providers.claude?.model : providers.gpt4?.model}
              </Text>
            </div>
          )}
        </div>
      ) : (
        <Alert
          type="warning"
          showIcon
          message="Aucun provider IA disponible"
          description="Contactez l'administrateur pour configurer les clés API (Claude ou OpenAI) sur le serveur."
          className="mb-4"
        />
      )}

      {/* Le citron met en avant le lancement de l’analyse sans modifier son traitement. */}
      <Button
        className="private-button-accent"
        type="primary"
        size="large"
        icon={<BulbOutlined />}
        onClick={handleAnalyze}
        loading={loading}
        disabled={!hasAvailableProvider || !selectedProvider}
      >
        Lancer l'analyse
      </Button>
    </div>
  );

  /**
   * Rendu de l'état de chargement
   */
  const renderLoadingState = () => (
    <div className="text-center py-12">
      <Spin size="large" />
      <Title level={5} className="mt-4">Analyse en cours...</Title>
      <Text type="secondary">
        {selectedProvider === 'claude' ? 'Claude' : 'GPT-4'} analyse vos données financières, veuillez patienter
      </Text>
    </div>
  );

  /**
   * Rendu de l'état d'erreur
   */
  const renderErrorState = () => {
    const isConfigError = error?.code === 'AI_NOT_CONFIGURED';
    const isWeeklyLimit = error?.code === 'AI_WEEKLY_LIMIT';

    return (
      <div className="py-4">
        <Alert
          type={isWeeklyLimit ? 'warning' : 'error'}
          showIcon
          icon={isWeeklyLimit ? <ExclamationCircleOutlined /> : <WarningOutlined />}
          message={
            isWeeklyLimit ? 'Limite hebdomadaire atteinte' :
            isConfigError ? 'Provider IA non disponible' :
            'Erreur lors de l\'analyse'
          }
          description={
            <div>
              <Paragraph className="mb-2">{error?.message}</Paragraph>
              {isConfigError && (
                <Text type="secondary">
                  Contactez l'administrateur pour vérifier la configuration du serveur.
                </Text>
              )}
              {isWeeklyLimit && (
                <Text type="secondary">
                  Vous pouvez essayer avec un autre provider IA si disponible.
                </Text>
              )}
            </div>
          }
        />
        <div className="text-center mt-4">
          {isWeeklyLimit ? (
            <Button onClick={handleReset}>
              Changer de provider
            </Button>
          ) : (
            <Button
              icon={<ReloadOutlined />}
              onClick={handleAnalyze}
              disabled={!hasAvailableProvider || !selectedProvider}
            >
              Réessayer
            </Button>
          )}
        </div>
      </div>
    );
  };

  /**
   * Formate une date ISO en format lisible français
   */
  const formatDateFr = (isoDate) => {
    if (!isoDate) return '';
    return new Date(isoDate).toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  /**
   * Rendu du résultat de l'analyse
   */
  const renderAnalysisResult = () => (
    <div className="py-2">
      {/* Bandeau si c'est une analyse précédente (limite hebdomadaire) */}
      {error?.code === 'AI_WEEKLY_LIMIT' && (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message="Analyse précédente"
          description={`Cette analyse a été réalisée le ${formatDateFr(error.analysisDate)}. Prochaine analyse disponible le ${formatDateFr(error.nextAvailableDate)}.`}
          className="mb-4"
        />
      )}

      {/* Score de santé */}
      <Card className="mb-4" size="small">
        <div className="flex items-center justify-between">
          <div>
            <Text type="secondary">Score de santé financière</Text>
            <Title level={2} className="mb-0" style={{ color: getHealthScoreColor(analysis.healthScore) }}>
              {analysis.healthScore}/100
            </Title>
          </div>
          <Progress
            type="circle"
            percent={analysis.healthScore}
            width={80}
            strokeColor={getHealthScoreColor(analysis.healthScore)}
            format={(percent) => (
              <span style={{ color: getHealthScoreColor(percent) }}>
                {percent >= 70 ? <CheckCircleOutlined /> :
                 percent >= 40 ? <ExclamationCircleOutlined /> :
                 <WarningOutlined />}
              </span>
            )}
          />
        </div>
      </Card>

      {/* Résumé */}
      <Card className="mb-4" size="small" title={<><InfoCircleOutlined /> Résumé</>}>
        <Paragraph className="mb-0">{analysis.summary}</Paragraph>
      </Card>

      {/* Alertes */}
      {analysis.warnings && analysis.warnings.length > 0 && (
        <Alert
          type="warning"
          icon={<WarningOutlined />}
          message="Alertes"
          description={
            <List
              size="small"
              dataSource={analysis.warnings}
              renderItem={(warning) => (
                <List.Item className="py-1 px-0 border-0">
                  <Text>• {warning}</Text>
                </List.Item>
              )}
            />
          }
          className="mb-4"
        />
      )}

      {/* Bandeaux citron pour les recommandations ; les priorités conservent leur couleur métier. */}
      <Divider orientation="left">
        <BulbOutlined /> Recommandations
      </Divider>

      <List
        dataSource={analysis.recommendations?.sort((a, b) => {
          // Trier par priorité : haute > moyenne > basse
          const priorityOrder = { haute: 0, moyenne: 1, basse: 2 };
          return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
        })}
        renderItem={(rec) => (
          <List.Item className="py-2">
            <Card size="small" className="w-full private-recommendation" bodyStyle={{ padding: '12px' }}>
              <div className="flex items-start gap-2">
                <span className="text-xl">{getCategoryIcon(rec.category)}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Text strong>{rec.title}</Text>
                    <Tag color={getPriorityColor(rec.priority)}>
                      {rec.priority}
                    </Tag>
                    <Tag>{rec.category}</Tag>
                  </div>
                  <Paragraph type="secondary" className="mb-1 text-sm">
                    {rec.description}
                  </Paragraph>
                  {rec.expectedImpact && (
                    <Text type="success" className="text-xs">
                      <RocketOutlined /> Impact attendu: {rec.expectedImpact}
                    </Text>
                  )}
                </div>
              </div>
            </Card>
          </List.Item>
        )}
      />

      {/* Opportunités */}
      {analysis.opportunities && analysis.opportunities.length > 0 && (
        <>
          <Divider orientation="left">
            <RocketOutlined /> Opportunités
          </Divider>
          <List
            size="small"
            dataSource={analysis.opportunities}
            renderItem={(opportunity) => (
              <List.Item className="py-1">
                <Space>
                  <RocketOutlined style={{ color: '#52c41a' }} />
                  <Text>{opportunity}</Text>
                </Space>
              </List.Item>
            )}
          />
        </>
      )}

      {/* Boutons en bas du résultat */}
      <div className="text-center mt-6">
        <Space>
          {/* Masquer "Relancer" si la limite hebdomadaire est atteinte pour ce provider */}
          {error?.code !== 'AI_WEEKLY_LIMIT' && (
            <Button
              icon={<ReloadOutlined />}
              onClick={handleAnalyze}
              loading={loading}
            >
              Relancer l'analyse
            </Button>
          )}
          <Button onClick={handleReset}>
            {error?.code === 'AI_WEEKLY_LIMIT' ? 'Essayer un autre provider' : 'Changer de provider'}
          </Button>
        </Space>
      </div>
    </div>
  );

  /**
   * Détermine le contenu à afficher
   * Si analyse + erreur weekly limit → afficher l'analyse précédente avec un bandeau
   */
  const renderContent = () => {
    if (loading) return renderLoadingState();
    // Si l'analyse existe (résultat frais ou analyse précédente récupérée), l'afficher
    if (analysis) return renderAnalysisResult();
    if (error) return renderErrorState();
    return renderInitialState();
  };

  return (
    <Modal
      title={
        <Space>
          <RobotOutlined />
          <span>Analyse IA de la rentabilité</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  );
};

export default AIAnalysisModal;
